import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

// Persisted per-browser (not per-workspace) — a user's light/dark preference is
// a personal setting, so it intentionally survives workspace switches and logout.
export const COLOR_MODE_STORAGE_KEY = 'pbdq_color_mode';
const STORAGE_KEY = COLOR_MODE_STORAGE_KEY;

const isValidMode = (m) => m === 'light' || m === 'dark';

/**
 * Initial mode resolution order:
 *   1. The user's own saved choice (the Advanced Settings toggle).
 *   2. Light.
 *
 * The OS / browser "prefers-color-scheme" is deliberately NOT followed. It made
 * the portal turn dark on its own for anyone whose OS is set to dark or to
 * "Auto" (macOS flips to dark in the evening) — a new account has no saved
 * choice, so the next full page load simply picked up the OS (SNW review,
 * 29 Sep). Dark mode is now only ever the user's explicit choice.
 */
const readInitialMode = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (isValidMode(saved)) return saved;
    } catch { /* ignore */ }
    return 'light';
};

const ColorModeContext = createContext({
    mode: 'light',
    isDark: false,
    toggleColorMode: () => {},
    setColorMode: () => {},
});

// custom hook
export const useColorMode = () => useContext(ColorModeContext);

/**
 * Holds the active light/dark mode and persists it. Sits ABOVE BrandingProvider
 * so both the brand CSS-variable effect and the MUI theme can react to the mode.
 */
export const ColorModeProvider = ({ children }) => {
    const [mode, setMode] = useState(readInitialMode);

    const persist = (value) => {
        try { localStorage.setItem(STORAGE_KEY, value); } catch { /* ignore quota */ }
    };

    const setColorMode = useCallback((next) => {
        const value = next === 'dark' ? 'dark' : 'light';
        setMode(value);
        persist(value);
    }, []);

    const toggleColorMode = useCallback(() => {
        setMode((prev) => {
            const value = prev === 'dark' ? 'light' : 'dark';
            persist(value);
            return value;
        });
    }, []);

    // Expose the mode to plain CSS too (e.g. `html[data-color-mode="dark"] .foo`).
    useEffect(() => {
        document.documentElement.setAttribute('data-color-mode', mode);
    }, [mode]);

    const value = useMemo(
        () => ({ mode, isDark: mode === 'dark', toggleColorMode, setColorMode }),
        [mode, toggleColorMode, setColorMode],
    );

    return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
};

export default ColorModeProvider;
